/**
 * CipherNest client crypto for CipherNest.
 * All vault secrets stay on-device; the Worker only stores EncStrings.
 */

const te = new TextEncoder();
const td = new TextDecoder();

function b64encode(buf: ArrayBuffer | Uint8Array): string {
  const bytes = buf instanceof Uint8Array ? buf : new Uint8Array(buf);
  let s = "";
  for (let i = 0; i < bytes.length; i++) s += String.fromCharCode(bytes[i]!);
  return btoa(s);
}

function b64decode(s: string): Uint8Array {
  const bin = atob(s);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

/** Fresh ArrayBuffer-backed view for SubtleCrypto BufferSource typing. */
function buf(u: Uint8Array): Uint8Array<ArrayBuffer> {
  const copy = new Uint8Array(u.byteLength);
  copy.set(u);
  return copy;
}

async function hmacSha256(key: Uint8Array, data: Uint8Array): Promise<ArrayBuffer> {
  const cryptoKey = await crypto.subtle.importKey(
    "raw",
    buf(key),
    { name: "HMAC", hash: "SHA-256" },
    false,
    ["sign"]
  );
  return crypto.subtle.sign("HMAC", cryptoKey, buf(data));
}

/**
 * CipherNest HKDF-Expand only (RFC 5869 §2.3).
 * Master key is used as PRK directly — NOT WebCrypto HKDF (which always Extracts first).
 */
async function hkdfExpand(
  prk: ArrayBuffer | Uint8Array,
  info: string,
  length: number
): Promise<Uint8Array> {
  const hashLen = 32;
  if (length > 255 * hashLen) throw new Error("hkdfExpand length too large");
  const prkBytes = prk instanceof Uint8Array ? prk : new Uint8Array(prk);
  const infoBytes = te.encode(info);
  const n = Math.ceil(length / hashLen);
  const okm = new Uint8Array(n * hashLen);
  let previousT = new Uint8Array(0);
  for (let i = 0; i < n; i++) {
    const t = new Uint8Array(previousT.length + infoBytes.length + 1);
    t.set(previousT);
    t.set(infoBytes, previousT.length);
    t[t.length - 1] = i + 1;
    previousT = new Uint8Array(await hmacSha256(prkBytes, t));
    okm.set(previousT, i * hashLen);
  }
  return okm.slice(0, length);
}

/** PBKDF2-SHA256 → raw bits (CipherNest master key / password hash). */
export async function pbkdf2(
  password: string | ArrayBuffer,
  salt: string | ArrayBuffer,
  iterations: number,
  length = 32
): Promise<ArrayBuffer> {
  const passKey = await crypto.subtle.importKey(
    "raw",
    typeof password === "string" ? te.encode(password) : password,
    "PBKDF2",
    false,
    ["deriveBits"]
  );
  return crypto.subtle.deriveBits(
    {
      name: "PBKDF2",
      hash: "SHA-256",
      salt: typeof salt === "string" ? te.encode(salt) : salt,
      iterations,
    },
    passKey,
    length * 8
  );
}

export type KdfParams = {
  kdf: number; // 0 = PBKDF2, 1 = Argon2id (not implemented in v1 browser crypto)
  kdfIterations: number;
  kdfMemory?: number | null;
  kdfParallelism?: number | null;
};

/** Derive 32-byte master key (PBKDF2; Argon2id falls back with clear error). */
export async function deriveMasterKey(
  email: string,
  masterPassword: string,
  kdf: KdfParams
): Promise<ArrayBuffer> {
  if (kdf.kdf === 1) {
    throw new Error(
      "Argon2id accounts are not supported in CipherNest v1 yet. Use PBKDF2 or migrate KDF."
    );
  }
  return pbkdf2(masterPassword, email.trim().toLowerCase(), kdf.kdfIterations, 32);
}

/**
 * Master password hash sent as OAuth `password` (CipherNest):
 * PBKDF2(masterKey, masterPassword, 1) → base64
 */
export async function masterPasswordHash(
  masterKey: ArrayBuffer,
  masterPassword: string
): Promise<string> {
  const hash = await pbkdf2(masterKey, masterPassword, 1, 32);
  return b64encode(hash);
}

export type SymmetricKey = {
  encKey: Uint8Array;
  macKey: Uint8Array;
  raw: Uint8Array;
};

/** Stretch 32-byte key → 64-byte enc+mac (CipherNest HKDF-Expand). */
export async function stretchKey(key: ArrayBuffer): Promise<SymmetricKey> {
  const enc = await hkdfExpand(key, "enc", 32);
  const mac = await hkdfExpand(key, "mac", 32);
  const raw = new Uint8Array(64);
  raw.set(enc, 0);
  raw.set(mac, 32);
  return { encKey: enc, macKey: mac, raw };
}

export async function stretchFromRaw64(raw64: Uint8Array): Promise<SymmetricKey> {
  return {
    encKey: raw64.slice(0, 32),
    macKey: raw64.slice(32, 64),
    raw: raw64,
  };
}

function constantTimeEqual(a: Uint8Array, b: Uint8Array): boolean {
  if (a.length !== b.length) return false;
  let d = 0;
  for (let i = 0; i < a.length; i++) d |= a[i]! ^ b[i]!;
  return d === 0;
}

/** Encrypt UTF-8 plaintext → EncString type 2 (AesCbc256_HmacSha256_B64). */
export async function encryptAes256Hmac(
  plaintext: string,
  key: SymmetricKey
): Promise<string> {
  const iv = crypto.getRandomValues(new Uint8Array(16));
  const encKey = await crypto.subtle.importKey(
    "raw",
    buf(key.encKey),
    { name: "AES-CBC" },
    false,
    ["encrypt"]
  );
  const ct = new Uint8Array(
    await crypto.subtle.encrypt({ name: "AES-CBC", iv }, encKey, te.encode(plaintext))
  );
  const macData = new Uint8Array(iv.length + ct.length);
  macData.set(iv, 0);
  macData.set(ct, iv.length);
  const mac = new Uint8Array(await hmacSha256(key.macKey, macData));
  return `2.${b64encode(iv)}|${b64encode(ct)}|${b64encode(mac)}`;
}

/** Decrypt EncString (supports type 0/1/2 common formats). */
export async function decryptEncString(
  encString: string | null | undefined,
  key: SymmetricKey
): Promise<string | null> {
  if (encString == null || encString === "") return null;
  const parts = encString.split(".");
  if (parts.length < 2) return null;
  const type = parts[0];
  const rest = parts.slice(1).join(".");
  const chunks = rest.split("|");

  if (type === "2" && chunks.length === 3) {
    const iv = b64decode(chunks[0]!);
    const ct = b64decode(chunks[1]!);
    const mac = b64decode(chunks[2]!);
    const macData = new Uint8Array(iv.length + ct.length);
    macData.set(iv, 0);
    macData.set(ct, iv.length);
    const expected = new Uint8Array(await hmacSha256(key.macKey, macData));
    if (!constantTimeEqual(mac, expected)) {
      throw new Error("MAC validation failed");
    }
    const encKey = await crypto.subtle.importKey(
      "raw",
      buf(key.encKey),
      { name: "AES-CBC" },
      false,
      ["decrypt"]
    );
    const pt = await crypto.subtle.decrypt(
      { name: "AES-CBC", iv: buf(iv) },
      encKey,
      buf(ct)
    );
    return td.decode(pt);
  }

  if ((type === "0" || type === "1") && chunks.length >= 2) {
    const iv = b64decode(chunks[0]!);
    const ct = b64decode(chunks[1]!);
    const encKey = await crypto.subtle.importKey(
      "raw",
      buf(key.encKey),
      { name: "AES-CBC" },
      false,
      ["decrypt"]
    );
    const pt = await crypto.subtle.decrypt(
      { name: "AES-CBC", iv: buf(iv) },
      encKey,
      buf(ct)
    );
    return td.decode(pt);
  }

  throw new Error(`Unsupported EncString type: ${type}`);
}

/** Decrypt EncString that wraps a 64-byte symmetric key (user key / cipher key). */
export async function decryptSymmetricKey(
  encString: string,
  key: SymmetricKey
): Promise<SymmetricKey> {
  const jsonOrB64 = await decryptEncString(encString, key);
  if (!jsonOrB64) throw new Error("Empty key");
  // Protected symmetric keys are raw 64 bytes base64 after decrypt in type-2 of key material
  // CipherNest stores decrypted payload as raw bytes encoded in the AES plaintext (64 bytes)
  // Our decryptEncString returns UTF-8 string — for key material use binary decrypt path
  return decryptSymmetricKeyBinary(encString, key);
}

async function decryptSymmetricKeyBinary(
  encString: string,
  key: SymmetricKey
): Promise<SymmetricKey> {
  const parts = encString.split(".");
  const type = parts[0];
  const chunks = parts.slice(1).join(".").split("|");
  if (type !== "2" || chunks.length !== 3) {
    throw new Error("Symmetric key must be EncString type 2");
  }
  const iv = b64decode(chunks[0]!);
  const ct = b64decode(chunks[1]!);
  const mac = b64decode(chunks[2]!);
  const macData = new Uint8Array(iv.length + ct.length);
  macData.set(iv, 0);
  macData.set(ct, iv.length);
  const expected = new Uint8Array(await hmacSha256(key.macKey, macData));
  if (!constantTimeEqual(mac, expected)) throw new Error("MAC validation failed");
  const encKey = await crypto.subtle.importKey(
    "raw",
    buf(key.encKey),
    { name: "AES-CBC" },
    false,
    ["decrypt"]
  );
  const pt = new Uint8Array(
    await crypto.subtle.decrypt(
      { name: "AES-CBC", iv: buf(iv) },
      encKey,
      buf(ct)
    )
  );
  if (pt.length === 64) return stretchFromRaw64(pt);
  if (pt.length === 32) return stretchKey(buf(pt).buffer);
  throw new Error(`Unexpected key length: ${pt.length}`);
}

export async function encryptSymmetricKey(
  keyToProtect: SymmetricKey,
  withKey: SymmetricKey
): Promise<string> {
  const iv = crypto.getRandomValues(new Uint8Array(16));
  const encKey = await crypto.subtle.importKey(
    "raw",
    buf(withKey.encKey),
    { name: "AES-CBC" },
    false,
    ["encrypt"]
  );
  const ct = new Uint8Array(
    await crypto.subtle.encrypt(
      { name: "AES-CBC", iv },
      encKey,
      buf(keyToProtect.raw)
    )
  );
  const macData = new Uint8Array(iv.length + ct.length);
  macData.set(iv, 0);
  macData.set(ct, iv.length);
  const mac = new Uint8Array(await hmacSha256(withKey.macKey, macData));
  return `2.${b64encode(iv)}|${b64encode(ct)}|${b64encode(mac)}`;
}

export async function generateSymmetricKey(): Promise<SymmetricKey> {
  const raw = crypto.getRandomValues(new Uint8Array(64));
  return stretchFromRaw64(raw);
}

export async function generatePassword(
  length = 20,
  opts: { upper?: boolean; lower?: boolean; digits?: boolean; special?: boolean } = {}
): Promise<string> {
  const upper = opts.upper !== false;
  const lower = opts.lower !== false;
  const digits = opts.digits !== false;
  const special = opts.special === true;
  let alphabet = "";
  if (lower) alphabet += "abcdefghijkmnopqrstuvwxyz";
  if (upper) alphabet += "ABCDEFGHJKLMNPQRSTUVWXYZ";
  if (digits) alphabet += "23456789";
  if (special) alphabet += "!@#$%^&*_-+=?";
  if (!alphabet) alphabet = "abcdefghijkmnopqrstuvwxyz23456789";
  const bytes = crypto.getRandomValues(new Uint8Array(length));
  let out = "";
  for (let i = 0; i < length; i++) {
    out += alphabet[bytes[i]! % alphabet.length]!;
  }
  return out;
}

/** Encrypt raw bytes → EncString type 2. */
export async function encryptBytes(
  plaintext: Uint8Array,
  key: SymmetricKey
): Promise<string> {
  const iv = crypto.getRandomValues(new Uint8Array(16));
  const encKey = await crypto.subtle.importKey(
    "raw",
    buf(key.encKey),
    { name: "AES-CBC" },
    false,
    ["encrypt"]
  );
  const ct = new Uint8Array(
    await crypto.subtle.encrypt(
      { name: "AES-CBC", iv },
      encKey,
      buf(plaintext)
    )
  );
  const macData = new Uint8Array(iv.length + ct.length);
  macData.set(iv, 0);
  macData.set(ct, iv.length);
  const mac = new Uint8Array(await hmacSha256(key.macKey, macData));
  return `2.${b64encode(iv)}|${b64encode(ct)}|${b64encode(mac)}`;
}

/** Decrypt EncString type 2 → raw bytes. */
export async function decryptBytes(
  encString: string,
  key: SymmetricKey
): Promise<Uint8Array> {
  const parts = encString.split(".");
  const type = parts[0];
  const chunks = parts.slice(1).join(".").split("|");
  if (type !== "2" || chunks.length !== 3) {
    throw new Error("Expected EncString type 2");
  }
  const iv = b64decode(chunks[0]!);
  const ct = b64decode(chunks[1]!);
  const mac = b64decode(chunks[2]!);
  const macData = new Uint8Array(iv.length + ct.length);
  macData.set(iv, 0);
  macData.set(ct, iv.length);
  const expected = new Uint8Array(await hmacSha256(key.macKey, macData));
  if (!constantTimeEqual(mac, expected)) throw new Error("MAC validation failed");
  const encKey = await crypto.subtle.importKey(
    "raw",
    buf(key.encKey),
    { name: "AES-CBC" },
    false,
    ["decrypt"]
  );
  return new Uint8Array(
    await crypto.subtle.decrypt(
      { name: "AES-CBC", iv: buf(iv) },
      encKey,
      buf(ct)
    )
  );
}

export type RsaKeyPairB64 = {
  publicKeyB64: string;
  privateKeyPkcs8: Uint8Array;
};

/** Generate RSA-2048 key pair (CipherNest account / org style). */
export async function generateRsaKeyPair(): Promise<RsaKeyPairB64> {
  const pair = await crypto.subtle.generateKey(
    {
      name: "RSA-OAEP",
      modulusLength: 2048,
      publicExponent: new Uint8Array([1, 0, 1]),
      hash: "SHA-1",
    },
    true,
    ["encrypt", "decrypt"]
  );
  const spki = new Uint8Array(await crypto.subtle.exportKey("spki", pair.publicKey));
  const pkcs8 = new Uint8Array(
    await crypto.subtle.exportKey("pkcs8", pair.privateKey)
  );
  return { publicKeyB64: b64encode(spki), privateKeyPkcs8: pkcs8 };
}

async function importRsaPublic(b64: string): Promise<CryptoKey> {
  return crypto.subtle.importKey(
    "spki",
    buf(b64decode(b64)),
    { name: "RSA-OAEP", hash: "SHA-1" },
    false,
    ["encrypt"]
  );
}

async function importRsaPrivate(pkcs8: Uint8Array): Promise<CryptoKey> {
  return crypto.subtle.importKey(
    "pkcs8",
    buf(pkcs8),
    { name: "RSA-OAEP", hash: "SHA-1" },
    false,
    ["decrypt"]
  );
}

/** EncString type 4: Rsa2048_OaepSha1_B64 */
export async function encryptRsaOaepSha1(
  plaintext: Uint8Array,
  publicKeyB64: string
): Promise<string> {
  const key = await importRsaPublic(publicKeyB64);
  const ct = new Uint8Array(
    await crypto.subtle.encrypt({ name: "RSA-OAEP" }, key, buf(plaintext))
  );
  return `4.${b64encode(ct)}`;
}

export async function decryptRsaOaepSha1(
  encString: string,
  privateKeyPkcs8: Uint8Array
): Promise<Uint8Array> {
  const parts = encString.split(".");
  if (parts[0] !== "4" || parts.length < 2) {
    throw new Error("Expected EncString type 4");
  }
  const ct = b64decode(parts.slice(1).join("."));
  const key = await importRsaPrivate(privateKeyPkcs8);
  return new Uint8Array(
    await crypto.subtle.decrypt({ name: "RSA-OAEP" }, key, buf(ct))
  );
}

/** Build account key material for registration / PUT /keys. */
export async function createProtectedAccountKeys(userKey: SymmetricKey) {
  const rsa = await generateRsaKeyPair();
  const wrappedPrivateKey = await encryptBytes(rsa.privateKeyPkcs8, userKey);
  return {
    publicKey: rsa.publicKeyB64,
    encryptedPrivateKey: wrappedPrivateKey,
  };
}

/** Org create payload: org RSA + symmetric key wrapped for owner. */
export async function createOrganizationKeys(ownerPublicKeyB64: string) {
  const orgRsa = await generateRsaKeyPair();
  const orgKey = await generateSymmetricKey();
  const encryptedPrivateKey = await encryptBytes(orgRsa.privateKeyPkcs8, orgKey);
  const ownerWrappedOrgKey = await encryptRsaOaepSha1(
    orgKey.raw,
    ownerPublicKeyB64
  );
  return {
    publicKey: orgRsa.publicKeyB64,
    encryptedPrivateKey,
    key: ownerWrappedOrgKey,
    orgKey,
  };
}

/** Re-wrap org symmetric key for an invitee public key. */
export async function wrapOrgKeyForUser(
  orgKeyRaw: Uint8Array,
  userPublicKeyB64: string
): Promise<string> {
  return encryptRsaOaepSha1(orgKeyRaw, userPublicKeyB64);
}

export { b64encode, b64decode };
